/**
 * Hook for managing dashboard filter state
 */

import { useState, useCallback, useMemo, useEffect } from 'react'
import dayjs from 'dayjs'
import { DashboardFilters, DashboardQueryParams } from '../types'

/**
 * localStorage key for toggle preference
 */
const TOGGLE_STORAGE_KEY = 'v4-dashboard-license-credential-toggle'

/**
 * Get saved toggle preference from localStorage
 */
function getSavedTogglePreference(): boolean {
  try {
    let saved = localStorage.getItem(TOGGLE_STORAGE_KEY)
    return saved ? JSON.parse(saved) : false
  } catch {
    return false
  }
}

/**
 * Save toggle preference to localStorage
 */
function saveTogglePreference(value: boolean): void {
  try {
    localStorage.setItem(TOGGLE_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Ignore localStorage errors (e.g., in private mode)
  }
}

/**
 * Initial filter state matching original dashboard
 */
let initialFilters: DashboardFilters = {
  linkedServiceID: '',
  timeRange: 'month',
  customStartDate: dayjs().subtract(1, 'month').toISOString(),
  customEndDate: dayjs().toISOString(),
  selectedUsers: [],
  selectedCategories: [],
  selectedLicenseTypes: [],
  selectedGroups: [],
  selectedActivityTypes: [],
  viewMode: 'summary',
  showLicenseCredentialEvents: getSavedTogglePreference(), // Load from localStorage
}

/**
 * Convert filter state to API query parameters
 */
function filtersToQueryParams(filters: DashboardFilters): DashboardQueryParams {
  let { customStartDate, customEndDate, timeRange } = filters

  // Calculate start/end dates based on timeRange
  let startDate = (() => {
    if (timeRange === 'custom' && customStartDate) {
      return dayjs(customStartDate).format('YYYY-MM-DD')
    }

    let now = dayjs()
    switch (timeRange) {
      case 'week':
        return now.subtract(1, 'week').format('YYYY-MM-DD')
      case 'month':
        return now.subtract(1, 'month').format('YYYY-MM-DD')
      case 'quarter':
        return now.subtract(3, 'months').format('YYYY-MM-DD')
      case 'year':
        return now.subtract(1, 'year').format('YYYY-MM-DD')
      default:
        return now.subtract(1, 'month').format('YYYY-MM-DD')
    }
  })()

  let endDate = (() => {
    if (timeRange === 'custom' && customEndDate) {
      return dayjs(customEndDate).format('YYYY-MM-DD')
    }
    return dayjs().format('YYYY-MM-DD')
  })()

  return {
    linkedServiceID: filters.linkedServiceID,
    startDate,
    endDate,
    userIDs:
      filters.selectedUsers.length > 0 ? filters.selectedUsers : undefined,
    categories:
      filters.selectedCategories.length > 0
        ? filters.selectedCategories
        : undefined,
    licenseTypes:
      filters.selectedLicenseTypes.length > 0
        ? filters.selectedLicenseTypes
        : undefined,
    groupIDs:
      filters.selectedGroups.length > 0 ? filters.selectedGroups : undefined,
    activityTypes:
      filters.selectedActivityTypes.length > 0
        ? filters.selectedActivityTypes
        : undefined,
  }
}

/**
 * Hook for managing dashboard filters
 */
export function useFilters() {
  let [filters, setFilters] = useState<DashboardFilters>(initialFilters)

  // Save toggle preference to localStorage whenever it changes
  useEffect(() => {
    saveTogglePreference(filters.showLicenseCredentialEvents)
  }, [filters.showLicenseCredentialEvents])

  // Update individual filter values
  let updateFilter = useCallback(
    <K extends keyof DashboardFilters>(key: K, value: DashboardFilters[K]) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }))
    },
    [],
  )

  // Reset all filters to defaults (but preserve toggle preference)
  let resetFilters = useCallback(() => {
    setFilters((prev) => ({
      ...initialFilters,
      showLicenseCredentialEvents: prev.showLicenseCredentialEvents, // Preserve toggle state
    }))
  }, [])

  // Convert current filters to query parameters (memoized for stable references)
  let queryParams = useMemo(() => filtersToQueryParams(filters), [filters])

  return {
    filters,
    queryParams,
    updateFilter,
    resetFilters,
    setFilters,
  }
}
